import JWTDecode from 'jwt-decode';
import { useCallback, useEffect, useMemo, useState } from 'react';
import GeckoRequest from 'services/gecko-request';

export const GeckoIntegrationsFactory = ({ getAuthTokenCallback }) => {
  const parseFetchBody = async (fetchResult) => {
    if (fetchResult.status === 204) {
      return null;
    }
    if (fetchResult.headers.get('Content-Type') === 'application/json') {
      return await fetchResult.json();
    }
    return await fetchResult.text();
  };

  const callFetch = (baseUrl, slug, config, authToken) => {
    return fetch(`${baseUrl}${slug}`, {
      ...config,
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });
  };

  const tokenRefreshRequired = (parsedResult) =>
    Array.isArray(parsedResult.errors) &&
    parsedResult.errors.find((err) => err.title === 'Token Has Expired');

  const integrationsFetch = async (slug, config) => {
    const authToken = await getAuthTokenCallback();
    const decodedToken = JWTDecode(authToken);
    const baseUrl =
      decodedToken.api || process.env.REACT_APP_INTEGRATIONS_BASE_URL;
    const fetchResult = await callFetch(baseUrl, slug, config, authToken);
    const parsedResult = await parseFetchBody(fetchResult);

    if (tokenRefreshRequired(parsedResult)) {
      // The Integrations service will try to refresh the token. So wait and then try the fetch again...
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const retryResult = await callFetch(baseUrl, slug, config, authToken);
      return await parseFetchBody(retryResult);
    } else {
      return parsedResult;
    }
  };

  const getIntegrations = async () => {
    const token = await getAuthTokenCallback();
    const { account } = JWTDecode(token);
    const result = await integrationsFetch(`/integrations/list/${account}`, {
      method: 'GET',
    });

    if (result.success) {
      const data = await Promise.all(
        result.data.map(async (integration) => {
          const nextChallengeResult = await getNextChallenge(
            integration.integrationId,
          );
          const isSetup =
            nextChallengeResult.success === true &&
            nextChallengeResult.data.challenge === null;
          return {
            ...integration,
            isSetup,
          };
        }),
      );
      return {
        ...result,
        data,
      };
    }

    if (
      result.errors.some(
        (error) =>
          error.detail === 'No integrations found for the specified account.',
      )
    ) {
      return {
        success: true,
        data: [],
        errors: [],
      };
    }

    return result;
  };

  const createIntegration = async (type) => {
    const token = await getAuthTokenCallback();
    const { account } = JWTDecode(token);
    const payload = {
      ownerId: account,
      primaryType: 'GeckoChat',
      secondaryType: type,
    };

    return await integrationsFetch('/integrations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  };

  const getNextChallenge = async (integrationId) => {
    const result = await integrationsFetch(
      `/integrations/${integrationId}/setup`,
      {
        method: 'GET',
      },
    );
    return result;
  };

  const submitChallengeResponse = async (
    integrationId,
    challenge,
    response,
  ) => {
    const body = {
      connectionCategory: challenge.connectionCategory,
      challengeName: challenge.challengeName,
      answer: response,
    };
    const result = await integrationsFetch(
      `/integrations/${integrationId}/setup`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
    return result;
  };

  const getContactSyncDetails = async (integrationId, contactId) => {
    const account = await GeckoRequest.get('/account');
    const result = await integrationsFetch(
      `/integrations/${integrationId}/syncs/primary/GeckoChat/${account.uuid}/contact/${contactId}`,
      {
        method: 'GET',
      },
    );
    return result;
  };

  const getConversationSyncDetails = async (integrationId, conversationId) => {
    const account = await GeckoRequest.get('/account');
    return integrationsFetch(
      `/integrations/${integrationId}/syncs/primary/GeckoChat/${account.uuid}/conversation/${conversationId}`,
      {
        method: 'GET',
      },
    );
  };

  const getIntegration = async (integrationId) => {
    return await integrationsFetch(`/integrations/${integrationId}`, {
      method: 'GET',
    });
  };

  const getModules = async (integrationId, connectionCategory) => {
    const result = await integrationsFetch(
      `/integrations/${integrationId}/${connectionCategory}/modules`,
      {
        method: 'GET',
      },
    );
    return result;
  };

  const getPrimaryModules = async (integrationId) => {
    return getModules(integrationId, 'primary');
  };

  const getSecondaryModules = async (integrationId) => {
    return getModules(integrationId, 'secondary');
  };

  const getAttributes = async (
    integrationId,
    connectionCategory,
    module = 'default',
  ) => {
    const result = await integrationsFetch(
      `/integrations/${integrationId}/${connectionCategory}/attributes/${module}`,
      {
        method: 'GET',
      },
    );
    return result;
  };

  const getPrimaryAttributes = async (integrationId, module = 'default') => {
    return getAttributes(integrationId, 'primary', module);
  };

  const getSecondaryAttributes = async (integrationId, module = 'default') => {
    return getAttributes(integrationId, 'secondary', module);
  };

  const createFieldMapping = async (
    integrationId,
    primaryAttribute,
    secondaryAttribute,
  ) => {
    const payload = {
      primaryAttributeId: primaryAttribute.id,
      primaryAttributeModule: primaryAttribute.module,
      primaryAttributeType: primaryAttribute.type,
      primaryAttributeLabel: primaryAttribute.label,
      primaryAttributeConfig: primaryAttribute.config,
      secondaryAttributeId: secondaryAttribute.id,
      secondaryAttributeModule: secondaryAttribute.module,
      secondaryAttributeLabel: secondaryAttribute.label,
      secondaryAttributeType: secondaryAttribute.type,
      secondaryAttributeConfig: secondaryAttribute.config,
    };

    return await integrationsFetch(`/integrations/${integrationId}/map-field`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  };

  const updateFieldMapping = async (
    integrationId,
    primaryAttribute,
    secondaryAttribute,
    fieldId,
  ) => {
    const payload = {
      primaryAttributeId: primaryAttribute.id,
      primaryAttributeModule: primaryAttribute.module,
      primaryAttributeType: primaryAttribute.type,
      primaryAttributeLabel: primaryAttribute.label,
      primaryAttributeConfig: primaryAttribute.config,
      secondaryAttributeId: secondaryAttribute.id,
      secondaryAttributeModule: secondaryAttribute.module,
      secondaryAttributeLabel: secondaryAttribute.label,
      secondaryAttributeType: secondaryAttribute.type,
      secondaryAttributeConfig: secondaryAttribute.config,
    };

    return await integrationsFetch(
      `/integrations/${integrationId}/mapped-fields/${fieldId}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  };

  const updateMatchingConditions = async (integrationId, body) => {
    return await integrationsFetch(
      `/integrations/${integrationId}/matching-conditions`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  };

  const deleteFieldMapping = async (integrationId, fieldMappingId) => {
    return await integrationsFetch(
      `/integrations/${integrationId}/mapped-fields/${fieldMappingId}`,
      {
        method: 'DELETE',
      },
    );
  };

  const deleteIntegration = async (integrationId) => {
    return await integrationsFetch(`/integrations/${integrationId}`, {
      method: 'DELETE',
    });
  };


  /**
   * @typedef {object} AddModuleRelationshipResponse
   * @property {boolean} success
   * @property {array} errors
   * @property {object} data
   * @property {string} data.moduleRelationshipId
   */

  /**
   * @typedef {object} AvailableModuleRelationship
   * @property {string} sourceCategory
   * @property {string} sourceModuleId
   * @property {string} sourceModuleLabel
   * @property {string} sourceDependencyModuleId
   * @property {string} sourceDependencyModuleLabel
   * @property {string} destinationModuleId
   * @property {string} destinationModuleLabel
   * @property {string} relatedModuleId
   * @property {string} relatedModuleLabel
   * @property {string} attributeId
   * @property {string} attributeLabel
   */

  /** @jsdoc
   * @param {string} integrationId
   * @param {AvailableModuleRelationship} availableModuleRelationship
   * @param {boolean} requiredForSync
   * @returns {Promise<AddModuleRelationshipResponse>}
   */
  const addModuleRelationship = async (
    integrationId,
    availableModuleRelationship,
    requiredForSync,
  ) => {

    const {
      sourceModuleId,
      sourceDependencyModuleId,
      destinationModuleId,
      relatedModuleId,
    } = availableModuleRelationship;

    const relationshipAttributeId = availableModuleRelationship.attributeId;

    const payload = {
      sourceModuleId,
      sourceDependencyModuleId,
      destinationModuleId,
      relatedModuleId,
      relationshipAttributeId,
      requiredForSync,
    };

    return await integrationsFetch(`/integrations/${integrationId}/module-relationships`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  };

  const deleteModuleRelationship = async (integrationId, moduleRelationshipId) => {
    return await integrationsFetch(`/integrations/${integrationId}/module-relationships/${moduleRelationshipId}`, {
      method: 'DELETE',
    });
  };

  return {
    integrationsFetch,
    getIntegrations,
    createIntegration,
    getNextChallenge,
    submitChallengeResponse,
    getContactSyncDetails,
    getConversationSyncDetails,
    getIntegration,
    getModules,
    getPrimaryModules,
    getSecondaryModules,
    getAttributes,
    getPrimaryAttributes,
    getSecondaryAttributes,
    createFieldMapping,
    updateMatchingConditions,
    deleteFieldMapping,
    deleteIntegration,
    updateFieldMapping,
    addModuleRelationship,
    deleteModuleRelationship,
  };
};

export const useGeckoIntegrations = ({ getAuthTokenCallback }) => {
  return useMemo(() => {
    return GeckoIntegrationsFactory({ getAuthTokenCallback });
  }, [getAuthTokenCallback]);
};

export const useGeckoIntegrationsFunction = (
  func,
  { initialValue = null, autoLoad = true } = {},
) => {
  const [data, setData] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const reload = useCallback(() => {
    setIsLoading(true);
    try {
      return func()
        .then((result) => {
          if (result.success === false) {
            setErrors(result.errors);
            return data;
          }
          setErrors([]);
          setData(result.data);
          return result.data;
        })
        .catch((error) => {
          console.error(error);
          setErrors([
            {
              title: 'Unknown Error',
              detail: 'An unknown error occured',
              code: 500,
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error(error);
      setErrors([
        {
          title: 'Unknown Error',
          detail: 'An unknown error occured',
          code: 500,
        },
      ]);
      setIsLoading(false);
    }
  }, [func, setData, setIsLoading, setErrors]);

  useEffect(() => {
    if (autoLoad === true) {
      reload();
    }
  }, [reload, autoLoad]);

  return {
    isLoading,
    data,
    errors,
    reload,
  };
};
